import React, { useEffect, useState } from 'react';
import styles from './Dashboard.module.scss';
import Pagebase from '@/components/organism/Pagebase';
import DashboardSummary from '@/components/organism/Dashboard/DashboardSummary';
import DashboardFilter from '@/components/organism/Dashboard/DashboardFilter';
import Datatable from '@/components/atomics/Datatable';
import { useDevice, useDownloadDevice } from '@/hooks/useDevice';
import Chips from '@/components/atomics/Chips';
import DashboardAction from '@/components/organism/Dashboard/DashboardAction';
import { getLocalStorage } from '@/utils/storages';
import DashboardOnvif from '@/components/organism/Dashboard/DashboardOnvif';
import { Tooltip } from '@/components/atomics';

type DeviceData = {
  nd_internet: string;
  sid: string;
  device_uuid: string;
  ndmain?: string;
  email?: string;
  emailMasked?: string;
  device_name?: string;
  device_category?: string;
  device_brand?: string;
  online?: string;
  status_subscription_device?: string;
  userId: string;
  serial_number: string;
  isEazy?: string;
  onvif_eligible?: boolean;
  sc_number?: string;
};

export type SelectedDevice = {
  brand?: string;
  userId?: string;
  deviceSerial?: string;
  deviceUUID?: string;
  deviceName?: string;
};

const className = 'dashboard';

const Dashboard: React.FC = () => {
  const [ndmain, setNdmain] = useState<string | undefined>(undefined);
  const [email, setEmail] = useState<string | undefined>(undefined);
  const [uuid, setUuid] = useState<string | undefined>(undefined);
  const [scNumber, setScNumber] = useState<string | undefined>(undefined);
  const [selectedDevices, setSelectedDevices] = useState<SelectedDevice[]>([]);
  const [page, setPage] = useState<number>(1);
  const [access, setAccess] = useState<string[]>([]);
  const [size, setSize] = useState<number>(10);
  const [showEmail, setShowEmail] = useState<boolean>(false);

  const { data, isLoading, refetch } = useDevice({
    ndmain,
    email,
    device_uuid:uuid,
    sc_number: scNumber,
    page,
    size
  });
  const { mutate: mutationDownload } = useDownloadDevice({
    ndmain,
    email,
    device_uuid: uuid,
    sc_number: scNumber,
    download: true
  });

  useEffect(() => {
    const level = JSON.parse(getLocalStorage('user_data') || '{}')?.level;

    if (level === 1) {
      setAccess(['isolir', 'unpair']);
    } else if (level === 2) {
      setAccess(['unpair']);
    } else if (level === 0) {
      setAccess(['isolir', 'unpair', 'onvif']);
    }
  }, []);

  const isDeviceSelected = (deviceUUID: string) =>
    selectedDevices.some(dev => dev.deviceUUID === deviceUUID);

  const getDeviceInfo = (item: DeviceData): SelectedDevice => (item?.device_brand === 'tuya' ? {
    userId: item?.userId,
    deviceSerial: item?.serial_number,
    deviceUUID: item?.device_uuid,
    deviceName: item?.device_name,
  } : {
    userId: item?.userId,
    deviceSerial: item?.serial_number,
    deviceName: item?.device_name,
  });

  const list: DeviceData[] = (data && typeof data === 'object' && 'list' in data && Array.isArray((data as any).list))
    ? (data as any).list
    : [];

  const totalPage = (data && typeof data === 'object' && 'totalPage' in data)
    ? (data as any).totalPage
    : 1;

  const renderEmptyData = () => (
    <>
      <p>
        <img src="/images/icon-nodata-filter.png" alt="No data" />
      </p>
      <label>
        Silakan masukkan nomor internet<br /> atau email pada kolom pencarian untuk menampilkan data.
      </label>
    </>
  );

  const handleFilterSearch = (params: { ndmain: string; email: string; uuid: string; sc_number: string }) => {
    setNdmain(params.ndmain);
    setEmail(params.email);
    setUuid(params.uuid);
    setScNumber(params.sc_number);
  };

  const getVariantChips = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'suspended':
        return 'warning';
      case 'isolir':
        return 'error';
      default:
        return 'info';
    }
  };

  return (
    <Pagebase className={styles[className]} onDownload={() => {      
      mutationDownload(undefined, {
        onSuccess: (data: string) => {
          // If the API returns a URL string directly
          window.open(data ?? '', '_blank');
        },
        onError: (error) => {
        },
      });
    }} title="Dashboard">
      {/* <DashboardSummary className={styles[className + '__summary']} /> */}
      <div className={styles[className + '__card']}>
        <DashboardFilter onSearch={handleFilterSearch} />
        <Datatable
          columns={[
            {
              label: (
                <div>
                  <input
                    onChange={e => {
                      if (e.target.checked) {
                        setSelectedDevices(
                          Array.isArray(list)
                            ? list.filter(item => item?.isEazy === 'true').map(item => getDeviceInfo(item))
                            : []
                        );
                      } else {
                        setSelectedDevices([]);
                      }
                    }}
                    checked={
                      Array.isArray(list) &&
                      list.length > 0 &&
                      list.every(item => isDeviceSelected(item.device_uuid))
                    }
                    type="checkbox"
                  />
                </div>
              ),
              key: 'check',
              width: '50px',
            },
            { label: 'No Internet', key: 'ndmain' },
            { label: 'No Digital', key: 'sid' },
            { label: 'E-mail', key: 'email' },
            { label: 'UUID', key: 'device_uuid' },
            { label: 'SC Number', key: 'sc_number' },
            { label: 'Nama Perangkat', key: 'device_name' },
            { label: 'Jenis Perangkat', key: 'device_category' },
            { label: 'Status Perangkat', key: 'device_status' },
            { label: 'Platform', key: 'platform' },
            { label: 'Status Langganan', key: 'status_subscription_device' },
            { label: 'Fitur ONVIF', key: 'onvif', tooltip: 'Aktifkan untuk menampilkan pengaturan fitur ONVIF di aplikasi Eazy pelanggan.',
              width: '170px', show: access.includes('onvif') },
          ]}
          data={
            Array.isArray(list)
              ? list.map(item => ({
                  check: (
                    <div>
                      <input
                        checked={isDeviceSelected(item?.device_uuid)}
                        disabled={item?.isEazy !== 'true'}
                        onChange={e => {
                          if (e.target.checked) {
                            setSelectedDevices(prev => [
                              ...prev,
                              getDeviceInfo(item),
                            ]);
                          } else {
                            setSelectedDevices(prev =>
                              prev.filter(dev => dev.deviceUUID !== item.device_uuid)
                            );
                          }
                        }}
                        type="checkbox"
                      />
                    </div>
                  ),
                  nd_internet: item?.ndmain,
                  sid: item?.sid,
                  sc_number: item?.sc_number,
                  ndmain: item?.ndmain,
                  email: (
                    <Tooltip position="bottom_center" message={item?.email} text={item?.emailMasked} type="text" />
                  ),
                  device_uuid: item?.device_uuid,
                  device_name: item?.device_name,
                  device_category: item?.device_category === 'suspended' ? 'grace period'
                    : item?.device_category,
                  device_status: item?.online === 'true' ? 'Online' : 'Offline',
                  platform: item?.isEazy === 'true' ? 'Eazy' : 'Indihome Eazy',
                  status_subscription_device: item?.status_subscription_device ? (
                    <Chips
                      label={item?.status_subscription_device}
                      variant={getVariantChips(item?.status_subscription_device ?? '')}
                    />
                  ) : '-',
                  onvif: <DashboardOnvif
                    checked={item?.onvif_eligible}
                    userId={item?.userId}
                    label="Enable ONVIF Access"
                    onChange={() => {
                      refetch();
                    }}
                  />
                }))
              : []
          }
          emptyData={renderEmptyData()} isLoading={isLoading}
          onChangeSize={(size) => {
            setSize(size);
            setPage(1);
            setSelectedDevices([]);
          }}
          onChangePage={(page) => {
            setPage(page);
            setSelectedDevices([]);
          }}
          page={page} 
          totalPage={totalPage}
        />
        {list.length > 0 && (<DashboardAction 
          access={access}
          data-testid="dashboard-action" 
          disabled={selectedDevices?.length === 0}
          onSubmit={() => {
            refetch();
            setSelectedDevices([]);
          }}
          selectedDevices={selectedDevices}
           />)}
      </div>
    </Pagebase>
  );
};

export default Dashboard;